import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Megaphone } from "lucide-react";
import {
    listCampaigns,
    createCampaign,
    listVerticalTrackers,
    CAMPAIGN_STATUS_COLORS,
} from "@/lib/chiroApi";

export default function Campaigns() {
    const [campaigns, setCampaigns] = useState([]);
    const [trackers, setTrackers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showNew, setShowNew] = useState(false);
    const [draft, setDraft] = useState({
        name: "",
        city: "",
        target_state: "",
        vertical_tracker_id: "chirotracker",
        target_business_type: "chiropractor",
        goals: "",
    });

    const load = async () => {
        setLoading(true);
        const [c, t] = await Promise.all([listCampaigns(), listVerticalTrackers()]);
        setCampaigns(c);
        setTrackers(t);
        setLoading(false);
    };

    useEffect(() => {
        load();
    }, []);

    const create = async () => {
        if (!draft.name.trim()) {
            toast.error("Campaign name is required");
            return;
        }
        try {
            const body = { ...draft, status: "draft" };
            // strip empties so backend defaults apply
            for (const k of Object.keys(body)) {
                if (body[k] === "") delete body[k];
            }
            await createCampaign(body);
            toast.success("Campaign created");
            setShowNew(false);
            setDraft({
                name: "",
                city: "",
                target_state: "",
                vertical_tracker_id: "chirotracker",
                target_business_type: "chiropractor",
                goals: "",
            });
            load();
        } catch {
            toast.error("Create failed");
        }
    };

    return (
        <div data-testid="campaigns-page" className="max-w-5xl">
            <div className="flex items-center justify-between mb-4">
                <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#7cc93c] mb-2">
                        // campaigns · what we are running
                    </div>
                    <h1 className="text-3xl font-bold tracking-tight">Campaigns</h1>
                </div>
                <button
                    onClick={() => setShowNew((v) => !v)}
                    data-testid="new-campaign-btn"
                    className="flex items-center gap-2 bg-[#7cc93c] text-[#090a0b] hover:bg-[#9be05c] font-mono text-[12px] uppercase tracking-[0.15em] px-4 py-2 rounded-[2px]"
                >
                    <Plus className="w-3.5 h-3.5" />
                    New campaign
                </button>
            </div>

            {showNew && (
                <div
                    className="mb-6 p-4 border border-[#262a2e] bg-[#0f1113] rounded-[2px]"
                    data-testid="new-campaign-form"
                >
                    <div className="grid grid-cols-2 gap-3">
                        <Input
                            label="Name"
                            value={draft.name}
                            onChange={(v) => setDraft({ ...draft, name: v })}
                            testid="new-campaign-name"
                            required
                        />
                        <Select
                            label="Vertical Tracker"
                            value={draft.vertical_tracker_id}
                            onChange={(v) => setDraft({ ...draft, vertical_tracker_id: v })}
                            testid="new-campaign-tracker"
                            options={trackers.map((t) => ({
                                value: t.id,
                                label: `${t.name}${t.status === "coming_later" ? " (soon)" : ""}`,
                                disabled: t.status === "coming_later",
                            }))}
                        />
                        <Input
                            label="City"
                            value={draft.city}
                            onChange={(v) => setDraft({ ...draft, city: v })}
                            testid="new-campaign-city"
                        />
                        <Input
                            label="Target state (e.g. TN)"
                            value={draft.target_state}
                            onChange={(v) => setDraft({ ...draft, target_state: v.toUpperCase() })}
                            testid="new-campaign-state"
                        />
                        <Input
                            label="Target business type"
                            value={draft.target_business_type}
                            onChange={(v) => setDraft({ ...draft, target_business_type: v })}
                            testid="new-campaign-business-type"
                        />
                        <div className="col-span-2">
                            <label className="block font-mono text-[11px] tracking-[0.2em] uppercase text-[#9ca3af] mb-1">
                                Goals
                            </label>
                            <textarea
                                rows={3}
                                data-testid="new-campaign-goals"
                                value={draft.goals}
                                onChange={(e) => setDraft({ ...draft, goals: e.target.value })}
                                className="w-full bg-[#0a0b0d] border border-[#262a2e] rounded-[2px] px-3 py-2 font-mono text-[12px] text-[#f3f4f6] focus:outline-none focus:border-[#7cc93c]"
                            />
                        </div>
                    </div>
                    <div className="mt-3 flex justify-end gap-2">
                        <button
                            onClick={() => setShowNew(false)}
                            data-testid="new-campaign-cancel"
                            className="font-mono text-[11px] uppercase tracking-[0.15em] px-3 py-2 border border-[#262a2e] text-[#9ca3af] hover:text-[#f3f4f6] rounded-[2px]"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={create}
                            data-testid="new-campaign-create"
                            className="font-mono text-[11px] uppercase tracking-[0.15em] px-3 py-2 bg-[#7cc93c] text-[#090a0b] hover:bg-[#9be05c] rounded-[2px]"
                        >
                            Create campaign
                        </button>
                    </div>
                </div>
            )}

            {loading && <div className="font-mono text-[12px] text-[#9ca3af]">Loading…</div>}

            {!loading && campaigns.length === 0 && (
                <div
                    className="p-8 border border-dashed border-[#262a2e] text-center text-[#9ca3af] font-mono text-[12px]"
                    data-testid="campaigns-empty"
                >
                    No campaigns yet. Create one to start sourcing prospects.
                </div>
            )}

            {!loading && campaigns.length > 0 && (
                <div className="space-y-2" data-testid="campaigns-list">
                    {campaigns.map((c) => (
                        <Link
                            key={c.id}
                            to={`/campaigns/${c.id}`}
                            data-testid={`campaign-row-${c.id}`}
                            className="flex items-center gap-4 p-4 border border-[#262a2e] bg-[#0f1113] hover:border-[#7cc93c] transition-colors rounded-[2px]"
                        >
                            <Megaphone className="w-4 h-4 text-[#7cc93c] shrink-0" />
                            <div className="flex-1 min-w-0">
                                <div className="font-bold text-[14px] truncate">{c.name}</div>
                                <div className="font-mono text-[10px] text-[#9ca3af] uppercase tracking-[0.15em] mt-0.5 truncate">
                                    {c.city || "—"} · {c.target_state || "—"} ·{" "}
                                    {c.target_business_type || "—"}
                                </div>
                            </div>
                            <div className="font-mono text-[10px] text-[#9ca3af]">
                                {c.prospect_count ?? 0} prospects
                            </div>
                            <span
                                className="font-mono text-[9px] uppercase tracking-[0.2em] px-2 py-[2px] border rounded-[2px]"
                                style={{
                                    color: CAMPAIGN_STATUS_COLORS[c.status] || "#9ca3af",
                                    borderColor: (CAMPAIGN_STATUS_COLORS[c.status] || "#262a2e") + "55",
                                }}
                            >
                                {c.status || "draft"}
                            </span>
                        </Link>
                    ))}
                </div>
            )}
        </div>
    );
}

function Input({ label, value, onChange, testid, required }) {
    return (
        <div>
            <label className="block font-mono text-[11px] tracking-[0.2em] uppercase text-[#9ca3af] mb-1">
                {label}
                {required && <span className="text-[#ff453a]"> *</span>}
            </label>
            <input
                type="text"
                data-testid={testid}
                value={value ?? ""}
                onChange={(e) => onChange(e.target.value)}
                className="w-full bg-[#0a0b0d] border border-[#262a2e] rounded-[2px] px-3 py-2 font-mono text-[12px] text-[#f3f4f6] focus:outline-none focus:border-[#7cc93c]"
            />
        </div>
    );
}

function Select({ label, value, onChange, testid, options }) {
    return (
        <div>
            <label className="block font-mono text-[11px] tracking-[0.2em] uppercase text-[#9ca3af] mb-1">
                {label}
            </label>
            <select
                data-testid={testid}
                value={value ?? ""}
                onChange={(e) => onChange(e.target.value)}
                className="w-full bg-[#0a0b0d] border border-[#262a2e] rounded-[2px] px-3 py-2 font-mono text-[12px] text-[#f3f4f6] focus:outline-none focus:border-[#7cc93c]"
            >
                {options.map((o) => (
                    <option key={o.value} value={o.value} disabled={o.disabled}>
                        {o.label}
                    </option>
                ))}
            </select>
        </div>
    );
}
